<template>
  <div id="rental-plan" class="content-section">
    <div class="section-title">租赁方案</div>
    <div class="section-content" v-loading="loading">
      <QSCommonTable
        :loading="loading"
        :data="rentalPlans"
        :columns="colList"
        :border="true"
        row-key="id"
        :pagination="false"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { Tooltip } from '@qs/ui'
import { use$to } from '@/utils/useUtils'
import { getPlanListByGoods } from '@/api/domain/shop'

const $to = use$to()

const props = defineProps({
  planIds: {
    type: Array,
    default: () => [],
  },
  cityCode: {
    type: String,
    default: '',
  },
})

const rentalPlans = ref([])
const loading = ref(false)

const getPlanList = async () => {
  if (!props.cityCode || !props.planIds?.length) return
  loading.value = true
  const [err, res] = await $to(
    getPlanListByGoods({
      ids: props.planIds,
      cityCode: props.cityCode,
    }),
  )
  loading.value = false
  if (err || res.code !== 1) return
  const { data = [] } = res
  rentalPlans.value = data?.items || []
}
// 表格列配置
const colList = computed(() => {
  return [
    {
      name: 'id',
      columnProps: {
        prop: 'id',
        label: '租赁方案ID',
      },
    },
    {
      name: 'name',
      columnProps: {
        prop: 'name',
        label: '租赁方案名称',
      },
    },
    {
      name: 'rentPeriod',
      columnProps: {
        prop: 'rentPeriod',
        label: '租期',
      },
      render: ({ row }) => {
        const { rentPeriod, rentPeriodType, rentPeriodTypeName } = row
        if (!rentPeriod) return '--'
        if (rentPeriodType === 1) {
          return `${rentPeriod}天`
        }
        return `${rentPeriod}个${rentPeriodTypeName}`
      },
    },
    {
      name: 'deposit',
      columnProps: {
        prop: 'deposit',
        label: '押金总额',
      },
    },
    {
      name: 'dailyRentAmount',
      columnProps: {
        prop: 'dailyRentAmount',
        label: '日均租金',
      },
      render: ({ row }) => {
        const { dailyRentAmount } = row
        if (!dailyRentAmount) return '--'
        return `${dailyRentAmount}元`
      },
    },
    {
      name: 'otherFeeList',
      columnProps: {
        prop: 'otherFeeList',
        label: '其他费项',
      },
      render: ({ row }) => {
        if (!row.otherFeeList || row.otherFeeList?.length === 0) return '--'
        if (row.otherFeeList?.length === 1) {
          return row.otherFeeList?.[0]
        }
        return (
          <div>
            <span>{row.otherFeeList?.[0]}</span>
            <Tooltip
              effect="dark"
              content={row.otherFeeList?.join('、')}
              placement="top"
            >
              <span class="a-hover-link">等{row.otherFeeList?.length}项</span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      name: 'presentDays',
      columnProps: {
        prop: 'presentDays',
        label: '赠送天数',
        width: '140px',
      },
      render: ({ row }) => {
        const { presentDays } = row
        if (presentDays === null) return '--'
        return `${presentDays}天`
      },
    },
  ]
})
watch(
  () => props.planIds,
  () => {
    getPlanList()
  },
)
</script>

<style lang="scss" scoped>
.content-section {
  background: #ffffff;
  border-radius: 12px;
  opacity: 1;
  margin-bottom: 12px;

  .section-title {
    font-size: 16px;
    font-weight: 500;
    color: #333333;
    padding: 8px 16px;
    line-height: 24px;
    letter-spacing: 0em;
    color: rgba(0, 0, 0, 0.9);
  }

  .section-content {
    padding: 0 16px 16px 16px;
  }
}

:deep(.el-table) {
  border-radius: 4px;

  th {
    padding: 8px 0px !important;
    opacity: 1;
    background: #f3f3f4;
    box-sizing: border-box;
    border-width: 0px 0px 1px 0px;
    border-style: solid;
    border-color: #eeeef0;
    font-family: PingFang SC;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0em;
    color: rgba(0, 0, 0, 0.6);
  }

  td {
    padding: 12px 0;
    color: #606266;
  }

  .extra-items {
    color: #366cfe;
    margin-left: 4px;
  }
}
</style>
